'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getLoanQuoteFromToken, postLoanPdfExtract } from '@/app/endpoints/loanComparatorEndpoints'
import { ILoanQuote } from '@/app/models/loanQuote'
import {
    LoanPdfExtractError,
    PDF_EXTRACT_ESTIMATED_MS,
    PDF_EXTRACT_MIN_REMAINING_S,
    PDF_EXTRACT_PROGRESS_CAP,
    PDF_EXTRACT_STEPS,
    PDF_EXTRACT_SUCCESS_REVEAL_MS,
    PDF_EXTRACT_TICK_MS,
    PDF_EXTRACT_TIMEOUT_MS,
} from '@/app/constants/loanPdfExtract'
import { diffImportedFields, ImportedFieldKey, mergeExtractedQuote } from '@/app/utils/loan/loan-pdf-extract-merge'

export type LoanPdfExtractStatus = 'idle' | 'analyzing' | 'success' | 'error'

export interface LoanPdfExtractResult {
    quote: ILoanQuote
    imported: Set<ImportedFieldKey>
}

interface UseLoanPdfExtractParams {
    token?: string
    getCurrentQuote: () => ILoanQuote | null
    onExtracted: (result: LoanPdfExtractResult) => void
    onStatusChange?: (status: LoanPdfExtractStatus) => void
    onError?: (code: string) => void
    getSignatureDate?: () => string
}

const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result).split(',')[1])
        reader.onerror = () => reject(new LoanPdfExtractError('FILE_UNREADABLE'))
        reader.readAsDataURL(file)
    })

export const useLoanPdfExtract = ({
    token,
    getCurrentQuote,
    onExtracted,
    onStatusChange,
    onError,
    getSignatureDate,
}: UseLoanPdfExtractParams) => {
    const [status, setStatus] = useState<LoanPdfExtractStatus>('idle')
    const [file, setFile] = useState<File | null>(null)
    const [errorCode, setErrorCode] = useState<string | null>(null)
    const [elapsedMs, setElapsedMs] = useState(0)
    const [stepIndex, setStepIndex] = useState(0)
    const [successQuote, setSuccessQuote] = useState<ILoanQuote | null>(null)
    const [attempts, setAttempts] = useState(0)

    const abortRef = useRef<AbortController | null>(null)
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
    // Une lecture abandonnée continue son FileReader : seul son numéro la distingue de la lecture en cours
    const runIdRef = useRef(0)
    const snapshotRef = useRef<ILoanQuote | null>(null)

    const stopTicking = useCallback(() => {
        if (tickRef.current) {
            clearInterval(tickRef.current)
            tickRef.current = null
        }
    }, [])

    const updateStatus = useCallback(
        (next: LoanPdfExtractStatus) => {
            setStatus(next)
            onStatusChange?.(next)
        },
        [onStatusChange]
    )

    // Toutes les sorties d'erreur passent par ici : c'est le seul point de mesure du taux d'échec
    const fail = useCallback(
        (code: string) => {
            setErrorCode(code)
            updateStatus('error')
            onError?.(code)
        },
        [updateStatus, onError]
    )

    useEffect(
        () => () => {
            runIdRef.current += 1
            abortRef.current?.abort()
            if (tickRef.current) clearInterval(tickRef.current)
        },
        []
    )

    const run = useCallback(
        async (nextFile: File) => {
            if (!token) return

            const runId = ++runIdRef.current
            const isStale = () => runIdRef.current !== runId
            setFile(nextFile)
            setErrorCode(null)
            setElapsedMs(0)
            setStepIndex(0)
            updateStatus('analyzing')

            const startedAt = Date.now()
            stopTicking()
            tickRef.current = setInterval(() => {
                const elapsed = Date.now() - startedAt
                setElapsedMs(elapsed)
                // L'étape finale n'est cochée qu'une fois le store écrit : elle reste « en cours » ici
                setStepIndex((current) =>
                    Math.max(
                        current,
                        PDF_EXTRACT_STEPS.findIndex((step) => elapsed < step.untilMs)
                    )
                )
            }, PDF_EXTRACT_TICK_MS)

            const controller = new AbortController()
            abortRef.current = controller
            const timeoutId = setTimeout(() => controller.abort(), PDF_EXTRACT_TIMEOUT_MS)

            snapshotRef.current = structuredClone(getCurrentQuote())
            const snapshot = snapshotRef.current
            let posted = false

            try {
                const pdfBase64 = await readFileAsBase64(nextFile)
                if (isStale()) return
                // Le backend compte la tentative dès l'appel, même si le client abandonne ensuite
                setAttempts((current) => current + 1)
                await postLoanPdfExtract(token, { pdf_base64: pdfBase64, pdf_name: nextFile.name }, controller.signal)
                posted = true

                const refreshed = await getLoanQuoteFromToken(token)
                if (isStale()) return
                if (!refreshed.loans?.length) {
                    throw new LoanPdfExtractError('NO_LOAN_FOUND')
                }

                const merged = snapshot ? mergeExtractedQuote(snapshot, refreshed, getSignatureDate?.()) : refreshed
                const imported = diffImportedFields(snapshot, merged)

                setStepIndex(PDF_EXTRACT_STEPS.length)
                stopTicking()
                await new Promise((resolve) => setTimeout(resolve, PDF_EXTRACT_SUCCESS_REVEAL_MS))
                if (isStale()) return

                setSuccessQuote(merged)
                onExtracted({ quote: merged, imported })
                updateStatus('success')
            } catch (error) {
                if (isStale()) return
                stopTicking()

                const isAbort = error instanceof DOMException && error.name === 'AbortError'
                // Un code explicite prime : NO_LOAN_FOUND est levé après un POST réussi
                fail(
                    error instanceof LoanPdfExtractError
                        ? error.code
                        : isAbort
                          ? 'TIMEOUT'
                          : posted
                            ? 'APPLIED_BUT_NOT_REFRESHED'
                            : 'EXTRACTION_FAILED'
                )
            } finally {
                clearTimeout(timeoutId)
                if (!isStale()) stopTicking()
            }
        },
        [token, getCurrentQuote, onExtracted, updateStatus, stopTicking, fail, getSignatureDate]
    )

    const reset = useCallback(() => {
        setErrorCode(null)
        setFile(null)
        updateStatus('idle')
    }, [updateStatus])

    // « Remplacer » : le client abandonne la lecture sans passer par l'état d'erreur
    const cancel = useCallback(() => {
        runIdRef.current += 1
        abortRef.current?.abort()
        stopTicking()
        setErrorCode(null)
        setElapsedMs(0)
        setStepIndex(0)
        updateStatus('idle')
    }, [stopTicking, updateStatus])

    const retry = useCallback(() => {
        if (file) {
            run(file)
            return
        }
        reset()
    }, [run, reset, file])

    const getSnapshot = useCallback(() => snapshotRef.current, [])

    const progress = useMemo(() => {
        const isComplete = stepIndex >= PDF_EXTRACT_STEPS.length
        return {
            ratio: isComplete ? 1 : Math.min(elapsedMs / PDF_EXTRACT_ESTIMATED_MS, PDF_EXTRACT_PROGRESS_CAP),
            remainingSeconds: Math.max(
                PDF_EXTRACT_MIN_REMAINING_S,
                Math.ceil((PDF_EXTRACT_ESTIMATED_MS - elapsedMs) / 1000)
            ),
            isOvertime: elapsedMs >= PDF_EXTRACT_ESTIMATED_MS,
        }
    }, [elapsedMs, stepIndex])

    return {
        status,
        file,
        fileName: file?.name ?? null,
        fileSize: file?.size ?? 0,
        errorCode,
        elapsedMs,
        stepIndex,
        progress,
        successQuote,
        attempts,
        run,
        fail,
        reset,
        cancel,
        retry,
        getSnapshot,
    }
}
